/**
 * Section 09a — Shaved Doji Strategy (R144).
 *
 * Pure CLH-close stop-and-reverse: flips LONG/SHORT every time the CLH close
 * direction changes. Mirrors backend/services/strategies/shavedDojiRules.ts and
 * the frontend overlay (utils/strategies/shavedDoji.ts) verbatim.
 */
import React from 'react';
import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import GuideCallout from '../GuideCallout';
import GuideExample from '../GuideExample';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_strategy_shaveddoji: GuideSection = {
  id: 'strategy-shaveddoji',
  label: 'Shaved Doji Strategy',
  group: 'strategies',
  icon: <SwapVertIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Shaved Doji Strategy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        The simplest in-house engine (R144): a <strong>pure CLH-close stop-and-reverse</strong>. It is always
        in the market — LONG or SHORT — and flips side the moment the CLH close changes direction. No bands,
        no scores, no confluence gates.
      </Typography>

      <GuideCallout kind="info" title="One engine, three surfaces">
        The same rule runs in the chart overlay (Strategies dropdown → Shaved Doji — markers + position-state
        colouring), the scheduled / manual Scan, and the alert it emits. They never diverge.
      </GuideCallout>

      <H id="rule">The rule</H>
      <P>
        On each <strong>closed CLH bar</strong> (the Custom-LinReg-HA close-only transform, computed regardless
        of the chart's display candle type) it compares this close to the previous CLH close:
      </P>
      <Box component="pre" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem', p: 1.25, my: 1, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 0.75 }}>{`CLH close > previous CLH close  →  BUY   (go LONG  — also covers any open short)
CLH close < previous CLH close  →  SHORT (go SHORT — also sells any open long)
CLH close == previous           →  carry the current direction (no flip)`}</Box>
      <P>
        It's a continuous flip: a BUY both covers a short and opens a long; a SHORT both sells a long and opens
        a short. A signal fires <strong>only on the flip</strong> — the bar where the running direction first
        changes — not on every bar that keeps going the same way. On the chart, bars are coloured by position
        state: green while LONG, red while SHORT, neutral while FLAT (only at the very start, before the first
        flip).
      </P>

      <H id="exits-sl">Exits, SL &amp; Target</H>
      <P>
        Shaved Doji has <strong>no strategy-level Stop-Loss or Target</strong> — its "exit" is simply the next
        opposite flip, and its emitted signals carry <code>isl: null</code> / <code>target: null</code> with a
        <code> LOW</code> strength label. Risk is still managed by the rest of the platform: when an auto-trade
        opens from a Shaved Doji signal it gets the investment-equivalent SL fallback (and you can add an ISL /
        Target from Trade Management or the chart), and the Trailing-SL engine applies as usual.
      </P>
      <GuideCallout kind="warning" title="Pairs with Auto-Reverse">
        Because Shaved Doji flips by emitting a single opposite-direction signal, the TradeDesk
        <strong> Auto-Reverse</strong> setting (Settings → TradeDesk, default ON) is what lets the auto-trade
        engine actually turn the position — it closes the open side, then opens the new one. With Auto-Reverse
        OFF an opposite flip is skipped (<code>POSITION_OPEN</code>) and the position is left as-is.
      </GuideCallout>

      <H id="character">Character &amp; when to use</H>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Always-in-the-market, high-turnover. Great for catching a clean trend early; whippy in chop because every CLH-close wiggle flips it." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="The CLH smoothing tames most micro-noise vs raw closes, but the higher the timeframe the fewer (and more meaningful) the flips. Validate the flip frequency on the chart overlay for your symbol/interval before auto-trading." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Best treated as a trend proxy / confirmation layer rather than a standalone hands-off engine on a fast timeframe." />
        </ListItem>
      </List>

      <H id="setup">How to enable Shaved Doji</H>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Open <OpenInApp to="/strategies" label="Strategies → Shaved Doji" /> and pick the watchlist(s) to scan.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Set the scan interval + cadence; run <strong>Scan Now</strong> or enable a scheduled scan (with its <em>Auto-Trade this scan</em> toggle if you want it to route to execution).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>If you'll auto-trade it, make sure <strong>Auto-Reverse</strong> is ON (Settings → TradeDesk) so the flips actually reverse the position, and set per-symbol <strong>Auto Long / Auto Short</strong> on the Instruments page.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>To eyeball it, open the symbol on Charts and choose <strong>Shaved Doji</strong> in the Strategy dropdown — the B / S markers there are identical to what the scan emits.</>} />
        </ListItem>
      </List>

      <GuideExample title="A worked Shaved Doji sequence">
        On the 1h chart, the CLH close has been falling for several bars → Shaved Doji is SHORT (red bars). A
        bar then closes <em>above</em> the previous CLH close → <strong>BUY</strong> fires: it covers the short
        and opens a long in one step (with Auto-Reverse ON). Bars turn green and it stays LONG while each CLH
        close keeps printing higher (no new signal — it's already long). The next time a CLH close prints below
        its predecessor → <strong>SHORT</strong> fires, selling the long and opening a short. There's no target
        to hit — you're flat only between the cover and the open, which happen on the same bar.
      </GuideExample>
    </>
  ),
  searchIndex: [
    { heading: 'The rule',        anchor: 'rule',       body: 'shaved doji clh close stop and reverse flip buy short greater less previous close' },
    { heading: 'Exits, SL & Target', anchor: 'exits-sl', body: 'shaved doji no stop loss target isl null low strength investment fallback trailing' },
    { heading: 'Character & when to use', anchor: 'character', body: 'shaved doji always in market high turnover trend chop whipsaw clh smoothing timeframe' },
    { heading: 'How to enable',   anchor: 'setup',      body: 'shaved doji setup enable strategies scan auto reverse auto long short chart overlay' },
  ],
};

export default Section_strategy_shaveddoji;
